import React, {useEffect, useState} from 'react';
import {Button, DatePicker, Drawer, Form, InputNumber, Modal, Select, Space, Tabs, Table, Grid} from 'antd';
import dayjs, {Dayjs} from 'dayjs';
import {Sporttypes} from "../generated/activity_pb";
import {activityTemplate, insertUpdateAction} from "../App";
import {Timestamp} from "google-protobuf/google/protobuf/timestamp_pb";
import {useAnnouncedMessage} from '@dowty/ui';
import {text} from '../utils/language';

const MAX_VALUE = 100000;
const LAST_ACTIVITY_KEY = 'lastActivityEntry';
type LastActivity = {sport: string; parameterOne: number; parameterTwo: number};
const readLastActivity = (): LastActivity | null => {
    try {
        const value = localStorage.getItem(LAST_ACTIVITY_KEY);
        if (!value) return null;
        const parsed = JSON.parse(value) as Partial<LastActivity>;
        if (typeof parsed.sport !== 'string'
            || typeof parsed.parameterOne !== 'number' || !Number.isFinite(parsed.parameterOne)
            || typeof parsed.parameterTwo !== 'number' || !Number.isFinite(parsed.parameterTwo)) return null;
        return {sport: parsed.sport, parameterOne: parsed.parameterOne, parameterTwo: parsed.parameterTwo};
    } catch {
        return null;
    }
};

const buildSubmission = (activeTab: string, selectedActivity: string | null, selectedDate: Dayjs | null, amount: number, weight: number, selectedTemplate: string | null, templates?: activityTemplate[]): insertUpdateAction | activityTemplate | null => {
    if (activeTab === '1') return {sport: selectedActivity, date: selectedDate?.format('DD-MM-YYYY') ?? '', parameterOne: amount, parameterTwo: weight, id: 0};
    if (!selectedTemplate || !templates) return null;
    const selected = templates.find(template => template.templateName === selectedTemplate);
    if (!selected) return null;
    return {templateName: selected.templateName, activities: selected.activities.map(activity => {
        activity.setTsinsert(selectedDate ? Timestamp.fromDate(selectedDate.hour(12).toDate()) : Timestamp.fromDate(dayjs().toDate()));
        return activity;
    })};
};

const AddActivityModal: React.FC<{
    isOpen: boolean;
    onCancel: () => void;
    onOk: (data: insertUpdateAction | activityTemplate) => void | boolean | Promise<void | boolean>;
    onDelete: (data: insertUpdateAction) => void | boolean | Promise<void | boolean>;
    mode: string;
    initialData?: insertUpdateAction | null;
    actualSportTypes: Sporttypes;
    isTemplateCreation: boolean;
    activityTemplate?: activityTemplate[];
}> = ({isOpen, onCancel, onOk, onDelete, mode, initialData, actualSportTypes, isTemplateCreation, activityTemplate}) => {
    const selectionMessageKey = 'activity-selection-required';
    const [selectedActivity, setSelectedActivity] = useState<string | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
    const [amount, setAmount] = useState(1);
    const [weight, setWeight] = useState(0);
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
    const [activeTab, setActiveTab] = useState('1');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [message, messageHolder] = useAnnouncedMessage();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const submitLabel = mode === 'edit' ? text('Speichern', 'Save') : text('Hinzufügen', 'Add');

    useEffect(() => {
        if (!isOpen) return;
        const lastActivity = readLastActivity();
        setSelectedActivity(initialData?.sport ?? lastActivity?.sport ?? null);
        setAmount(initialData?.parameterOne ?? lastActivity?.parameterOne ?? 1);
        setWeight(initialData?.parameterTwo ?? lastActivity?.parameterTwo ?? 0);
        setSelectedDate(initialData?.date ? dayjs(initialData.date, 'DD-MM-YYYY') : dayjs());
        setSaving(false);
        setDeleting(false);
    }, [initialData, isOpen]);

    const submit = async () => {
        if (activeTab === '1' && !selectedActivity) {
            message.error({key: selectionMessageKey, content: text('Bitte eine Aktivität auswählen.', 'Select an activity.')});
            return;
        }
        const data = buildSubmission(activeTab, selectedActivity, selectedDate, amount, weight, selectedTemplate, activityTemplate);
        if (!data) {
            if (activeTab === '2') message.error(text('Bitte eine Vorlage auswählen.', 'Select a template.'));
            return;
        }
        if (activeTab === '1' && (!Number.isInteger(amount) || amount < 1 || !Number.isInteger(weight) || weight < 0 || amount > MAX_VALUE || weight > MAX_VALUE)) {
            message.error(text('Die erste Angabe muss mindestens 1 sein, alle Werte höchstens 100.000 (ganze Zahlen).', 'The first value must be at least 1 and no value above 100,000 (whole numbers).'));
            return;
        }
        if (activeTab === '1' && selectedActivity) localStorage.setItem(LAST_ACTIVITY_KEY, JSON.stringify({sport: selectedActivity, parameterOne: amount, parameterTwo: weight}));

        setSaving(true);
        try {
            if (await onOk(data) === false) setSaving(false);
            else message.destroy(selectionMessageKey);
        } catch {
            message.error(text('Aktivität konnte nicht gespeichert werden. Bitte erneut versuchen.', 'Unable to save activity. Please try again.'));
            setSaving(false);
        }
    };

    const changeAmount = (setter: React.Dispatch<React.SetStateAction<number>>, value: number, delta: number) =>
        setter(Math.max(0, value + delta));

    const renderNumberInput = (label: string, value: number, setter: React.Dispatch<React.SetStateAction<number>>) => (
        <Form.Item label={label} extra={text('Ganze Zahl bis 100.000', 'Whole number up to 100,000')}>
            <Space.Compact block className="sf-number-input">
                <Button aria-label={text(`${label} verringern`, `Decrease ${label}`)} onClick={() => changeAmount(setter, value, -1)}>-</Button>
                <InputNumber className="sf-number-value" aria-label={label} value={value} min={0} max={MAX_VALUE} onChange={next => setter(next ?? 0)} />
                <Button aria-label={text(`${label} erhöhen`, `Increase ${label}`)} onClick={() => changeAmount(setter, value, 1)}>+</Button>
            </Space.Compact>
        </Form.Item>
    );

    const renderDate = () => <Form.Item label={text('Datum', 'Date')}>
        <DatePicker aria-label={text('Datum', 'Date')} value={selectedDate} onChange={setSelectedDate} disabledDate={date => date.isAfter(dayjs(), 'day')} format={text('DD.MM.YYYY', 'YYYY-MM-DD')} />
    </Form.Item>;

    const activityForm = <Form layout="vertical">
        <Form.Item label={text('Aktivität', 'Activity')}>
            <Select
                aria-label={text('Aktivität', 'Activity')}
                placeholder={text('Aktivität auswählen', 'Select activity')}
                value={selectedActivity}
                onChange={value => {
                    setSelectedActivity(value);
                    message.destroy(selectionMessageKey);
                }}
                showSearch
                optionFilterProp="children"
            >
                {actualSportTypes?.getSportypeList().map(sport => (
                    <Select.Option key={sport.getSporttypename()} value={sport.getSporttypename()}>{sport.getSporttypename()}</Select.Option>
                )) ?? <>
                    <Select.Option value="Running">Running</Select.Option>
                    <Select.Option value="Cycling">Cycling</Select.Option>
                    <Select.Option value="Swimming">Swimming</Select.Option>
                </>}
            </Select>
        </Form.Item>
        {actualSportTypes?.getSportypeList().filter(sport => sport.getSporttypename() === selectedActivity).map(sport => <React.Fragment key={sport.getSporttypename()}>
            {renderNumberInput(sport.getParameterone(), amount, setAmount)}
            {sport.getParametertwo() && renderNumberInput(sport.getParametertwo(), weight, setWeight)}
        </React.Fragment>)}
        {renderDate()}
    </Form>;

    const selectedTemplateActivities = activityTemplate?.find(template => template.templateName === selectedTemplate)?.activities ?? [];
    const templateForm = <Form layout="vertical">
        <Form.Item label="Template">
            <Select aria-label={text('Vorlage', 'Template')} placeholder="Select Template" value={selectedTemplate} onChange={setSelectedTemplate} showSearch optionFilterProp="children">
                {activityTemplate?.map(template => <Select.Option key={template.templateName} value={template.templateName}>{template.templateName}</Select.Option>)}
            </Select>
        </Form.Item>
        {renderDate()}
        <Table
            dataSource={selectedTemplateActivities.map(activity => ({
                id: activity.getId(),
                sport: activity.getSportype()?.getSporttypename(),
                parameterOne: activity.getParameterone(),
                parameterTwo: activity.getParametertwo(),
            }))}
            columns={[
                {title: 'Sport', dataIndex: 'sport', key: 'sport'},
                {title: 'Amount', dataIndex: 'parameterOne', key: 'parameterOne'},
                {title: 'Weight', dataIndex: 'parameterTwo', key: 'parameterTwo'},
            ]}
            pagination={{pageSize: 10}}
            rowKey="id"
        />
    </Form>;

    const footer = <Space className="sf-activity-modal-footer">
        {mode === 'edit' && <Button danger loading={deleting} disabled={saving} onClick={() => Modal.confirm({
            title: text('Aktivität löschen?', 'Delete activity?'),
            content: text('Diese Aktivität wird dauerhaft gelöscht.', 'This activity will be permanently deleted.'),
            okText: text('Löschen', 'Delete'),
            cancelText: text('Abbrechen', 'Cancel'),
            okButtonProps: {danger: true},
            onOk: async () => {
                setDeleting(true);
                try {
                    const deleted = await onDelete({
                        id: initialData?.id || 0,
                        sport: selectedActivity,
                        date: selectedDate?.valueOf() || '',
                        parameterOne: amount,
                        parameterTwo: weight,
                    });
                    if (deleted === false) setDeleting(false);
                } catch (error) {
                    setDeleting(false);
                    throw error;
                }
            },
        })}>{text('Löschen', 'Delete')}</Button>}
        <Button onClick={onCancel}>{text('Abbrechen', 'Cancel')}</Button>
        <Button type="primary" loading={saving} onClick={submit}>{submitLabel}</Button>
    </Space>;

    const content = <>
        {messageHolder}
        {isTemplateCreation ? activityForm : <Tabs className="sf-activity-tabs" activeKey={activeTab} onChange={setActiveTab} items={[
            {key: '1', label: 'Activity', children: activityForm},
            {key: '2', label: 'Template', children: templateForm},
        ]} />}
    </>;

    return isMobile ? (
        <Drawer className="sf-add-activity-drawer" title={text('Aktivität hinzufügen', 'Add Activity')} aria-label={text('Aktivität hinzufügen', 'Add Activity')} open={isOpen} onClose={onCancel} placement="bottom" height="85vh" footer={footer}>
            {content}
        </Drawer>
    ) : (
        <Modal className="sf-add-activity-modal" title={text('Aktivität hinzufügen', 'Add Activity')} open={isOpen} onCancel={onCancel} footer={footer} width={480}>
            {content}
        </Modal>
    );
};

export default AddActivityModal;
